import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useIsMobile } from "../../hooks/use-mobile";
import { AuthService } from "../../lib/auth";
import {
  isExperimentDirectEntryUser,
  startFreshExperimentDirectInterview,
} from "../../lib/experiment-direct-entry";
import { queryClient } from "../../lib/queryClient";
import type { UserProfile } from "../../types";
import { IconHome, IconLogout, IconTools, NavButton } from "../HeaderAtoms";
import { StudentHeader } from "./StudentHeader";

/**
 * 講座選択・課題説明・面接・評価などの画面上部に固定する Avia Dental ヘッダー。
 * ロゴの行き先と右側のボタンを、ログイン状態とロールで切り替える。
 */
export function StudentAppHeader() {
  const [location, navigate] = useLocation();
  const authService = AuthService.getInstance();
  const isMobile = useIsMobile();

  const { data: authStatus, isLoading } = useQuery({
    queryKey: ["authStatus"],
    queryFn: () => authService.getAuthStatus(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: currentUser } = useQuery<UserProfile | null>({
    queryKey: ["currentUser"],
    queryFn: () => authService.getCurrentUser(),
    enabled: authStatus?.isAuthenticated === true,
    staleTime: 5 * 60 * 1000,
  });

  const isAuthenticated = authStatus?.isAuthenticated ?? false;
  const effectiveUser = currentUser ?? authStatus?.user ?? null;
  const role = effectiveUser?.role ?? null;
  const isStudent = role === "student";
  const isStaffManager = role === "admin" || role === "educator";
  const isDirectEntry =
    isExperimentDirectEntryUser(effectiveUser) && Boolean(effectiveUser?.experimentEntryTarget);

  const handleLogoClick = async () => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (isDirectEntry && effectiveUser?.experimentEntryTarget) {
      if (location !== "/interview") {
        try {
          await startFreshExperimentDirectInterview(effectiveUser.experimentEntryTarget);
          queryClient.invalidateQueries({ queryKey: ["/api/avatar/current"] });
        } catch (error) {
          console.error("実証実験の直接面接開始に失敗しました:", error);
        }
      }
      navigate("/interview");
      return;
    }
    navigate(isStudent ? "/mypage/home" : "/select-filter");
  };

  // 管理者はスマホでは管理画面が使いにくいのでマイページへ戻す（従来どおり）。
  const managerHomePath = role === "educator" ? "/teacher" : isMobile ? "/mypage" : "/admin";
  const managerHomeLabel = role === "educator" ? "教員ページ" : isMobile ? "マイページ" : "管理画面";

  const actions = isLoading || !isAuthenticated ? null : isStaffManager ? (
    <NavButton
      icon={isMobile && role === "admin" ? <IconHome /> : <IconTools />}
      label={managerHomeLabel}
      variant="outline"
      onClick={() => navigate(managerHomePath)}
    />
  ) : !isStudent ? (
    // ほかにログアウトの手段がない利用者だけ、ヘッダーに残す。
    <NavButton
      icon={<IconLogout />}
      label="ログアウト"
      variant="outline"
      onClick={() => {
        void authService.logout();
      }}
    />
  ) : null;

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
      <StudentHeader
        onLogoClick={() => {
          void handleLogoClick();
        }}
        logoLabel="トップへ戻る"
        actions={actions}
        showStudentIcons={!isLoading && isAuthenticated && isStudent}
        profileCurrent={location.startsWith("/mypage/profile")}
        onProfileClick={() => navigate("/mypage/profile")}
      />
    </div>
  );
}
